import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { penTypes } from '../../../Definitions/valueTypes'
import { colors, glyphs } from '../../../Styles'

import { getSectionById } from '../../../Assets/TestData/sectionsData'
import { Button, ButtonList, PEN_EDIT } from '../..'
import { ButtonTypes } from '../../../Definitions'

interface Section_Props {
  sectionId: number
  onSaveBtn?: () => void
  onCancelBtn?: () => void
}
/**
 * SECTION_EDIT
 * @param sectionId Unique identifier for each section.
 * @param onSaveBtn OnClick event to handle Save btn.
 * @param onCancelBtn OnClick event to handle Cancel btn.
 * @returns a section of pens for Freeda Location Feeder systems.
 */
export default function SECTION_EDIT({ sectionId, onSaveBtn, onCancelBtn }: Section_Props) {
  const {t} = useTranslation()
  const section = getSectionById(sectionId);
  const [animalsToEdit, setAnimalsToEdit] = useState<string[]>([]);
  const [checkedPens, setCheckedPens] = useState<number[]>([]);
  const [isAllSelected, setIsAllSelected] = useState(false);

  function CancelBtnClick() {
    if (onCancelBtn) onCancelBtn()
  }

  function SaveBtnClick() {
    if (onSaveBtn) onSaveBtn()
  }

  // Error throwback if no section number excists
  if (!section) {
    return <div>Section {sectionId} not found</div>;
  }

    // Handle checking/unchecking pens
    const handlePenCheck = (penId: number, isChecked: boolean) => {
      setCheckedPens(prevChecked => {
        const updatedPens = isChecked ? [...prevChecked, penId] : prevChecked.filter(p => p !== penId);
        
        const newAnimalIds = section?.pens
            .filter(pen => updatedPens.includes(pen.penId) && pen.animal)
            .map(pen => pen.animal?.animalId?.toString()) // Convert animalId to string
            .filter((animalId): animalId is string => animalId !== undefined) || []; // Filter out undefined
  
        setAnimalsToEdit(newAnimalIds); // Update to array
        return updatedPens;
      });
    };
  
  // Handle checking/unchecking all pens
  const handleCheckAllPens = () => {
    if (isAllSelected) {
      setCheckedPens([]);
      setAnimalsToEdit([]);
    } else { // if single animal is already checked and "select all" is clicked
      const updatedPens = section?.pens
        .filter(pen => pen.animal)
        .map(pen => pen.penId) || [];
        
      const newAnimalIds = section?.pens
        .filter(pen => pen.animal)
        .map(pen => pen.animal?.animalId?.toString())
        .filter((animalId): animalId is string => animalId !== undefined) || [];
        
      setCheckedPens(updatedPens);
      setAnimalsToEdit(newAnimalIds);
    }
    setIsAllSelected(!isAllSelected);
  };

  const editModeBtns = 
  <div style={{ display: "flex", flexDirection: "row", justifyContent: "space-between", width: "100%"}}>
    <Button 
      content={!isAllSelected? t("buttons.selectAll") : t("buttons.deselectAll")} 
      startEnhancer={glyphs.Check({fillColor: colors.systemGreen})} 
      buttonType={ButtonTypes.tableButton} onClick={handleCheckAllPens}
      overrides={{
        baseButton: {backgroundColor: colors.greyLightPrimary},
        hoverStyles: {backgroundColor: colors.greyLightSecondary},
        contentStyles: {color: colors.greyDarkPrimary}
      }}
    /> 
    <ButtonList 
      contents={[t("buttons.saveBtn"), t("buttons.cancelBtn"), t("buttons.add"), t("buttons.move"), t("buttons.edit")]} 
      onClick={(i => {
        if (i === 0) {SaveBtnClick()} // TODO CTO: Save to db and swith section edit
        else if (i === 1) {CancelBtnClick()}
      })}
      buttonType={ButtonTypes.tableButton}
      startEnhancers={[
        glyphs.Check({fillColor: colors.greyDarkPrimary}), 
        glyphs.Close({fillColor: colors.greyDarkPrimary}),
        glyphs.Add({fillColor: colors.greyDarkPrimary}), 
        glyphs.AnimalMove({fillColor: colors.greyDarkPrimary}), 
        glyphs.Edit({fillColor: colors.greyDarkPrimary})
      ]}
      disabled={[false, false, true, true, true]}
      overrides={(index) => ({
        baseButton: {
          backgroundColor: index === 4 ? colors.greyLightSecondary : colors.greyLightPrimary
        },
        hoverStyles: {
          backgroundColor: colors.greyLightSecondary
        },
        disabledStyles: {
          opacity: index === 4 ? 1 : 0.5
        },
        contentStyles: {
          color: colors.greyDarkPrimary
        }
      })}
    />

  </div>

  const renderContent = () => {
    return section.pens.map(pen => (
      <PEN_EDIT
        key={pen.animal?.animalId}
        penId={pen.penId}
        penType={penTypes.advanced}
        onCheck={(isChecked) => handlePenCheck(pen.penId, isChecked)}
        isChecked={checkedPens.includes(pen.penId)}
      />
    ));
  }

  return (
      <div style={{ width: "fit-content", height: "fit-content" }}>
        <div style={{ display: "flex", flexDirection: "row", justifyContent: "space-between", alignItems: "end", padding: "0px 20px" }}>
          {editModeBtns}
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "", width: "fit-content", gap: "10px", borderRadius: "10px", overflow: "hidden" }}>
          {renderContent()}
        </div>
      </div>
  );
}